import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionSettings } from '@app/context/SessionSettingsContext'

import { CheckIcon } from '@ui/icons/Check.icon'
import { ContrastIcon } from '@ui/icons/ContrastIcon'
import { MoonIcon } from '@ui/icons/Moon.icon'
import { SunIcon } from '@ui/icons/Sun.icon'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'

import { Theme } from '@global/theme/Theme'

import { useThemeTracker } from '@analytics/amplitude/theme'

export const themeIconMap: Record<Theme, IconFCType> = {
  auto: ContrastIcon,
  dark: MoonIcon,
  light: SunIcon,
}

const themeOptions = [
  { theme: 'auto' as const, label: 'Theme.System Settings' },
  { theme: 'light' as const, label: 'Theme.Light Theme' },
  { theme: 'dark' as const, label: 'Theme.Dark Theme' },
]

export const useThemeOptions = () => {
  const [{ theme: currentTheme }, setSettings] = useSessionSettings()
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { themeChanged } = useThemeTracker()

  return useMemo(
    () =>
      themeOptions.map(({ theme, label }) => ({
        id: `profile-theme-${theme}`,
        label: t(label),
        icon: themeIconMap[theme],
        onClick: () => {
          if (theme !== currentTheme) {
            themeChanged({
              new_theme: theme,
              previous_theme: currentTheme,
              theme_changed_source: 'header',
            })
          }
          setSettings({ theme })
        },
        closeOnSelect: true,
        isSelected: currentTheme === theme,
        rightIcon: currentTheme === theme && isMobile ? CheckIcon : undefined,
      })),
    [currentTheme, setSettings, t, themeChanged, isMobile]
  )
}
